import { Title } from '@solidjs/meta'
import { createSignal, For } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import MapView from '~/components/Map'
import type { MapCircleType, MapMarkerType, MapPolygonType, MapPolylineType } from '~/types/Map'

const MapPage = () => {
  const [markers, setMarkers] = createSignal<MapMarkerType[]>([{ position: [4.711, -74.072], title: 'Bogotá', popup: 'Capital de Colombia', color: '#6366f1' }])

  const addMarker = () => {
    const lat = 4.6 + Math.random() * 0.2
    const lng = -74.1 + Math.random() * 0.2
    const colors = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6']
    setMarkers(prev => [...prev, { position: [lat, lng], title: `Marcador ${prev.length + 1}`, popup: `Lat: ${lat.toFixed(3)}, Lng: ${lng.toFixed(3)}`, color: colors[prev.length % colors.length] }])
  }

  const clearMarkers = () => setMarkers([])

  const bogotaMarkers: MapMarkerType[] = [
    { position: [4.711, -74.072], title: 'Centro', popup: 'Centro de Bogotá', color: '#6366f1' },
    { position: [4.601, -74.066], title: 'Monserrate', popup: 'Cerro de Monserrate', color: '#ef4444' },
    { position: [4.662, -74.053], title: 'Chorro de Quevedo', popup: 'Histórico Chorro de Quevedo', color: '#10b981' },
    { position: [4.658, -74.101], title: 'Parque Simón Bolívar', popup: 'El parque más grande', color: '#f59e0b' }
  ]

  const worldCities: MapMarkerType[] = [
    { position: [4.711, -74.072], title: 'Bogotá', popup: 'Colombia', color: '#6366f1' },
    { position: [-33.868, 151.209], title: 'Sydney', popup: 'Australia', color: '#ef4444' },
    { position: [48.856, 2.352], title: 'París', popup: 'Francia', color: '#3b82f6' },
    { position: [35.676, 139.65], title: 'Tokio', popup: 'Japón', color: '#f59e0b' },
    { position: [40.712, -74.006], title: 'Nueva York', popup: 'EE.UU.', color: '#10b981' }
  ]

  const routePoints: MapPolylineType[] = [
    {
      points: [
        [4.711, -74.072],
        [4.662, -74.053],
        [4.601, -74.066],
        [4.658, -74.101],
        [4.711, -74.072]
      ],
      color: '#6366f6',
      weight: 4,
      dashArray: '10 5'
    }
  ]

  const circles: MapCircleType[] = [
    { center: [4.711, -74.072], radius: 2000, color: '#6366f1', fillColor: '#6366f1', fillOpacity: 0.1 },
    { center: [4.601, -74.066], radius: 500, color: '#ef4444', fillColor: '#ef4444', fillOpacity: 0.15 }
  ]

  const polygons: MapPolygonType[] = [
    {
      points: [
        [4.72, -74.08],
        [4.72, -74.06],
        [4.7, -74.06],
        [4.7, -74.08]
      ],
      color: '#10b981',
      fillColor: '#10b981',
      fillOpacity: 0.1
    }
  ]

  return (
    <>
      <Title>Map Component - Florez Jaramillo</Title>
      <Container class="py-8">
        <div class="mb-10 mt-6">
          <h1 class="text-4xl font-bold text-foreground mb-4">Map</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">Mapa interactivo con controles Material You: zoom, capas, GPS, medición, rutas, áreas y pantalla completa.</p>
        </div>
        <div class="space-y-12">
          <Card>
            <Card.Header
              title="Mapa completo"
              subtitle="Todos los controles activos: zoom, capas, GPS, medición, pantalla completa."
              icon={<Icon icon="material-symbols-light:map-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <MapView center={[4.711, -74.072]} zoom={12} height="450px" markers={bogotaMarkers} fitBounds scaleControl />
            </Card.Body>
          </Card>
          <Card>
            <Card.Header
              title="Rutas y áreas"
              subtitle="Polylines, círculos y polígonos dibujados en el mapa."
              icon={<Icon icon="material-symbols-light:route" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <MapView center={[4.711, -74.072]} zoom={12} height="400px" markers={bogotaMarkers} polylines={routePoints} circles={circles} polygons={polygons} fitBounds />
            </Card.Body>
          </Card>
          <Card>
            <Card.Header title="Mapa mundial" subtitle="Ciudades con fitBounds." icon={<Icon icon="material-symbols-light:public" class="text-primary-500" size={24} />} />
            <Card.Body>
              <MapView zoom={2} height="400px" markers={worldCities} fitBounds />
            </Card.Body>
          </Card>
          <Card>
            <Card.Header
              title="Controles desactivables"
              subtitle="Activa o desactiva cada control por separado."
              icon={<Icon icon="material-symbols-light:settings-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <p class="text-sm font-medium text-foreground mb-2">Solo zoom + GPS</p>
                  <MapView center={[4.711, -74.072]} zoom={12} height="250px" layerControl={false} fullscreenControl={false} measureControl={false} scaleControl={false} />
                </div>
                <div>
                  <p class="text-sm font-medium text-foreground mb-2">Mapa limpio (sin controles)</p>
                  <MapView
                    center={[4.711, -74.072]}
                    zoom={12}
                    height="250px"
                    zoomControl={false}
                    layerControl={false}
                    fullscreenControl={false}
                    geolocationControl={false}
                    measureControl={false}
                    scaleControl={false}
                  />
                </div>
              </div>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header
              title="Marcadores dinámicos"
              subtitle="Agrega y elimina marcadores."
              icon={<Icon icon="material-symbols-light:add-location-alt-outline" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="flex gap-2 mb-4">
                <Button size="sm" onClick={addMarker} prependIcon="material-symbols-light:add">
                  Agregar
                </Button>
                <Button size="sm" variant="soft" color="danger" onClick={clearMarkers}>
                  Limpiar
                </Button>
              </div>
              <MapView center={[4.711, -74.072]} zoom={11} height="400px" markers={markers()} fitBounds={markers().length > 0} />
              <p class="text-xs text-foreground-subtle mt-2">{markers().length} marcador(es)</p>
            </Card.Body>
          </Card>
          <Card>
            <Card.Header title="API Reference" subtitle="Propiedades del componente Map." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b border-border">Propiedad</th>
                      <th class="px-6 py-4 border-b border-border">Tipo</th>
                      <th class="px-6 py-4 border-b border-border">Default</th>
                      <th class="px-6 py-4 border-b border-border">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <For each={apiRows}>
                      {row => (
                        <tr>
                          <td class="px-6 py-4 font-mono text-primary font-bold">{row.prop}</td>
                          <td class="px-6 py-4 text-foreground-subtle">{row.type}</td>
                          <td class="px-6 py-4 font-mono">{row.def}</td>
                          <td class="px-6 py-4 text-foreground-muted">{row.desc}</td>
                        </tr>
                      )}
                    </For>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

const apiRows = [
  { prop: 'center', type: '[number, number]', def: '[4.711, -74.072]', desc: 'Centro del mapa [lat, lng].' },
  { prop: 'zoom', type: 'number', def: '12', desc: 'Nivel de zoom (1-20).' },
  { prop: 'markers', type: 'MapMarkerType[]', def: '[]', desc: 'Marcadores con posición, título, popup y color.' },
  { prop: 'polylines', type: 'MapPolylineType[]', def: '[]', desc: 'Líneas/rutas: points, color, weight, dashArray.' },
  { prop: 'circles', type: 'MapCircleType[]', def: '[]', desc: 'Círculos: center, radius, color, fillColor.' },
  { prop: 'polygons', type: 'MapPolygonType[]', def: '[]', desc: 'Polígonos: points, color, fillColor.' },
  { prop: 'height', type: 'string', def: '"400px"', desc: 'Altura del mapa (CSS).' },
  {
    prop: 'theme',
    type: 'MapThemeType',
    def: "'google'",
    desc: 'Tema: light/dark/satellite/terrain/google/mapbox. En modo oscuro de la app, las capas claras (Google, Estándar, Terreno) se invierten con filtro CSS.'
  },
  {
    prop: 'layers',
    type: 'MapLayerOptionType[]',
    def: '5 por defecto',
    desc: 'Layers para el selector. Incluye Google/Mapbox si se pasa token. Cada layer acepta invertOnDark (invertir en modo oscuro) y maxNativeZoom (último zoom con tiles propios).'
  },
  {
    prop: 'interactive',
    type: 'boolean',
    def: 'true',
    desc: 'Si el mapa es interactuable. En pantallas táctiles un dedo desplaza la página (no el mapa) y el pellizco acerca; a pantalla completa el mapa recupera todos los gestos.'
  },
  { prop: 'zoomControl', type: 'boolean', def: 'true', desc: 'Mostrar controles de zoom.' },
  { prop: 'layerControl', type: 'boolean', def: 'true', desc: 'Mostrar selector de capas.' },
  { prop: 'fullscreenControl', type: 'boolean', def: 'true', desc: 'Mostrar botón de pantalla completa.' },
  { prop: 'geolocationControl', type: 'boolean', def: 'true', desc: 'Mostrar botón de ubicación GPS.' },
  { prop: 'measureControl', type: 'boolean', def: 'true', desc: 'Mostrar herramienta de medición de distancia.' },
  { prop: 'scaleControl', type: 'boolean', def: 'false', desc: 'Mostrar barra de escala.' },
  { prop: 'mapboxToken', type: 'string', def: '-', desc: 'Token de Mapbox para habilitar su layer.' },
  { prop: 'googleApiKey', type: 'string', def: '-', desc: 'API key de Google Maps para habilitar su layer.' },
  { prop: 'onMapClick', type: '(latlng) => void', def: '-', desc: 'Callback al hacer click en el mapa.' },
  { prop: 'onMarkerClick', type: '(marker, i) => void', def: '-', desc: 'Callback al hacer click en un marcador.' },
  { prop: 'onPolylineClick', type: '(polyline, i) => void', def: '-', desc: 'Callback al hacer click en una ruta.' },
  { prop: 'onCircleClick', type: '(circle, i) => void', def: '-', desc: 'Callback al hacer click en un círculo.' },
  { prop: 'onLayerChange', type: '(layer) => void', def: '-', desc: 'Callback al cambiar de layer.' },
  { prop: 'onGeolocate', type: '(pos) => void', def: '-', desc: 'Callback al geolocalizar.' },
  { prop: 'onMeasure', type: '(dist, pts) => void', def: '-', desc: 'Callback con distancia medida (meters).' },
  { prop: 'onReady', type: '(map) => void', def: '-', desc: 'Callback cuando el mapa está listo.' }
]

export default MapPage
